import { Rows } from 'common/types/scalarSpreadsheet';
import { underScoreSpaces } from 'pages/Allocation/allocations/utilities/util';
import generateEquityAllocationKey from 'pages/ValuationsAllocation/allocation/EquityAllocation/config/util/generate-equity-allocation-key';
import {
  RANGE_SENSITIVITY_ENTITY_HEADER_PRIMARY,
  RANGE_SENSITIVITY_SPREADSHEET_COMPANY_SECTION_HEADER,
  RANGE_SENSITIVITY_SPREADSHEET_EQUITY_VALUE,
  RANGE_SENSITIVITY_SPREADSHEET_FUND_ROW,
  RANGE_SENSITIVITY_SPREADSHEET_FUND_SECTION_HEADER,
  RANGE_SENSITIVITY_SPREADSHEET_HEADER,
  RANGE_SENSITIVITY_SPREADSHEET_SHARE_SECTION_HEADER,
  RANGE_SENSITIVITY_SPREADSHEET_TABLE,
} from 'pages/ValuationsAllocation/common/constants/rangeSensitivity/sheetAliases';
import {
  generateColumnKey,
  getObjectValue,
  getSlugValue,
  getStringValue,
  sortByOrderAndId,
} from 'utilities';
import { CustomRowConfigParams, RangeSensitivityRow } from './types';
import {
  COMPANY_VALUE_RANGE_SENSITIVITY_HEADER,
  FUND_VALUE_RANGE_SENSITIVITY_HEADER,
  RANGE_SENSITIVITY_LABEL,
  RANGE_SENSITIVITY_STATIC_CUSTOM_FORMAT,
  SHARE_VALUE_RANGE_SENSITIVITY_HEADER,
} from '../../constants';
import { getRangeSensitivityFundRows } from '../../utils';

const customRowConfig = (params: CustomRowConfigParams): Rows => {
  const { companyName, securities, weightedShareValues } = params;

  const rows: Rows = [
    {
      alias: RANGE_SENSITIVITY_SPREADSHEET_HEADER,
      className: `${RANGE_SENSITIVITY_SPREADSHEET_TABLE}-${getSlugValue(
        RANGE_SENSITIVITY_SPREADSHEET_HEADER
      )} bold large-header`,
      gridType: 'string',
      largeHeader: true,
      ignoreRowCopy: true,
      readOnly: true,
      value: RANGE_SENSITIVITY_LABEL,
    } as RangeSensitivityRow,
    {
      alias: RANGE_SENSITIVITY_SPREADSHEET_COMPANY_SECTION_HEADER,
      className: `${RANGE_SENSITIVITY_SPREADSHEET_TABLE}-${getSlugValue(
        RANGE_SENSITIVITY_SPREADSHEET_COMPANY_SECTION_HEADER
      )} row-label bold-row division-top-only`,
      gridType: 'string',
      hasSubRows: true,
      showSubRows: true,
      ignoreRowCopy: true,
      readOnly: true,
      value: COMPANY_VALUE_RANGE_SENSITIVITY_HEADER,
    } as RangeSensitivityRow,
    {
      alias: RANGE_SENSITIVITY_SPREADSHEET_EQUITY_VALUE,
      className: `subtitle-indented ${RANGE_SENSITIVITY_SPREADSHEET_TABLE}-${getSlugValue(
        RANGE_SENSITIVITY_SPREADSHEET_EQUITY_VALUE
      )}`,
      gridType: 'string',
      ignoreRowCopy: true,
      parent: RANGE_SENSITIVITY_SPREADSHEET_COMPANY_SECTION_HEADER,
      readOnly: true,
      value: `Total Equity Value of ${companyName}`,
      expr: '=summary.weighted_equity_value',
    } as RangeSensitivityRow,
  ];

  const fundEntries = getRangeSensitivityFundRows(weightedShareValues);

  if (fundEntries.length > 0) {
    rows.push({
      alias: RANGE_SENSITIVITY_SPREADSHEET_FUND_SECTION_HEADER,
      className: `${RANGE_SENSITIVITY_SPREADSHEET_TABLE}-${getSlugValue(
        RANGE_SENSITIVITY_SPREADSHEET_FUND_SECTION_HEADER
      )} row-label bold-row division-top-only`,
      gridType: 'string',
      hasSubRows: true,
      showSubRows: true,
      ignoreRowCopy: true,
      readOnly: true,
      value: FUND_VALUE_RANGE_SENSITIVITY_HEADER,
    });

    fundEntries.forEach(wsv => {
      const fund = getObjectValue(wsv.fund);
      rows.push({
        alias: `${RANGE_SENSITIVITY_SPREADSHEET_FUND_ROW}_${fund.id}`,
        className: `subtitle-indented ${RANGE_SENSITIVITY_SPREADSHEET_TABLE}-${getSlugValue(
          `${RANGE_SENSITIVITY_SPREADSHEET_FUND_ROW}_${fund.id}`
        )}`,
        gridType: 'string',
        ignoreRowCopy: true,
        parent: RANGE_SENSITIVITY_SPREADSHEET_FUND_SECTION_HEADER,
        readOnly: true,
        value: getStringValue(fund.name),
      });
    });
  }

  const securitiesOrdered = sortByOrderAndId(
    (securities ?? []).map(security => ({
      ...security,
      order: security.order ?? undefined,
    }))
  );

  if (securitiesOrdered.length > 0) {
    const equityAllocationSheetKeyForGroup = generateEquityAllocationKey();

    rows.push(
      {
        alias: RANGE_SENSITIVITY_SPREADSHEET_SHARE_SECTION_HEADER,
        className: `${RANGE_SENSITIVITY_SPREADSHEET_TABLE}-${getSlugValue(
          RANGE_SENSITIVITY_SPREADSHEET_SHARE_SECTION_HEADER
        )} row-label bold-row division-top-only`,
        gridType: 'string',
        hasSubRows: true,
        showSubRows: true,
        ignoreRowCopy: true,
        readOnly: true,
        value: SHARE_VALUE_RANGE_SENSITIVITY_HEADER,
      },
      {
        alias: RANGE_SENSITIVITY_ENTITY_HEADER_PRIMARY,
        className: `${RANGE_SENSITIVITY_SPREADSHEET_TABLE}-${getSlugValue(
          RANGE_SENSITIVITY_ENTITY_HEADER_PRIMARY
        )}`,
        gridType: 'string',
        ignoreRowCopy: true,
        parent: RANGE_SENSITIVITY_SPREADSHEET_SHARE_SECTION_HEADER,
        readOnly: true,
        value: companyName,
      }
    );

    securitiesOrdered.forEach(securityRow => {
      const securityName = getStringValue(getObjectValue(securityRow).name);
      if (!securityName) {
        return;
      }

      const shareRowAlias = generateColumnKey({
        name: securityName,
        prefix: RANGE_SENSITIVITY_ENTITY_HEADER_PRIMARY,
      });

      rows.push({
        alias: shareRowAlias,
        className: `subtitle-indented ${RANGE_SENSITIVITY_SPREADSHEET_TABLE}-${getSlugValue(
          shareRowAlias
        )}`,
        customFormat: RANGE_SENSITIVITY_STATIC_CUSTOM_FORMAT,
        customKey: underScoreSpaces(securityName),
        data: {
          equityAllocationSheetKey: equityAllocationSheetKeyForGroup,
          isRangeSensitivityShareSecurityRow: true,
        },
        gridType: 'string',
        ignoreRowCopy: true,
        parent: RANGE_SENSITIVITY_ENTITY_HEADER_PRIMARY,
        readOnly: true,
        value: securityName,
      });
    });
  }

  return rows;
};

export default customRowConfig;
